import { FreshRecipe } from "@api-clients/subscriptions";

import { Allergen } from "@/primary-models";

export interface IFreshRecipeDetails {
  name: string;
  description: string;
  imageSrc: string;
  allergens: string[];
  forKittens: boolean;
}

export type FreshRecipeDetailsMap = Record<FreshRecipe, IFreshRecipeDetails>;

export const IMAGE_BASE_URL = "/images/features/product-overview/packaging/";

const ALL_FRESH_RECIPE_DETAILS: FreshRecipeDetailsMap = {
  SHREDDED_CLUCK: {
    name: "CLUCK (SHREDDED)",
    description: "100% fresh shredded chicken thigh, liver and heart.",
    imageSrc: "shredded-cluck.webp",
    allergens: [ Allergen.CHICKEN, Allergen.SALMON ],
    forKittens: true,
  },
  SHREDDED_GOBBLE: {
    name: "GOBBLE (SHREDDED)",
    description: "100% fresh shredded turkey thigh and livers with chicken thigh and gizzards.",
    imageSrc: "shredded-gobble.webp",
    allergens: [ Allergen.TURKEY, Allergen.CHICKEN, Allergen.SALMON ],
    forKittens: true,
  },
  CLUCK: {
    name: "Cluck",
    description: "Fresh, 100% gently-cooked chicken thigh, liver and heart.",
    imageSrc: "cluck_2025.webp",
    allergens: [ Allergen.CHICKEN ],
    forKittens: true,
  },
  GOBBLE: {
    name: "Gobble",
    description: "Fresh, 100% gently-cooked turkey thigh, liver and chicken thigh and gizzards.",
    imageSrc: "gobble_2026.webp",
    allergens: [ Allergen.TURKEY, Allergen.CHICKEN ],
    forKittens: true,
  },
  QUACK: {
    name: "Quack",
    description: "Fresh, 100% gently-cooked chicken heart, thigh and liver, duck meat and gizzards.",
    imageSrc: "quack_2025.webp",
    allergens: [ Allergen.DUCK, Allergen.CHICKEN, Allergen.SALMON ],
    forKittens: true,
  },
  OINK: {
    name: "Oink",
    description: "Fresh, 100% gently-cooked pork shoulder, trim and liver.",
    imageSrc: "oink_2025.webp",
    allergens: [ Allergen.PORK, Allergen.SALMON ],
    forKittens: true,
  },
  MOOO: {
    name: "Mooo",
    description: "Fresh, 100% gently-cooked beef trim, heart and liver.",
    imageSrc: "mooo_2026.webp",
    allergens: [ Allergen.BEEF, Allergen.SALMON ],
    forKittens: true,
  },
  BAAA: {
    name: "Baaa",
    description: "Fresh, 100% gently-cooked lamb heart, trim, liver and kidney.",
    imageSrc: "baaa_2025.webp",
    allergens: [ Allergen.LAMB, Allergen.BEEF, Allergen.SALMON ],
    forKittens: false,
  },
  SPLASH: {
    name: "Splash",
    description: "Fresh, 100% gently-cooked whitefish, salmon and turkey liver.",
    imageSrc: "splash_2025.webp",
    allergens: [ Allergen.WHITEFISH, Allergen.TURKEY, Allergen.SALMON ],
    forKittens: true,
  },
  SWISH: {
    name: "Swish",
    description: "Fresh, 100% gently-cooked tuna, salmon, chicken heart and liver.",
    imageSrc: "swish_2026.webp",
    allergens: [ Allergen.TUNA, Allergen.SALMON, Allergen.CHICKEN ],
    forKittens: true,
  },
};

export const getFreshRecipeDetails = (isShreddedEnabled: boolean): Record<string, IFreshRecipeDetails> => {
  if (isShreddedEnabled) {
    return ALL_FRESH_RECIPE_DETAILS;
  }

  const {
    SHREDDED_CLUCK: _shreddedCluck,
    SHREDDED_GOBBLE: _shreddedGobble,
    ...nonShreddedRecipes
  } = ALL_FRESH_RECIPE_DETAILS;

  return nonShreddedRecipes;
};

export const getFreshRecipeDisplayName = (recipe: string, fallbackName?: string): string => (
  ALL_FRESH_RECIPE_DETAILS[recipe as FreshRecipe]?.name ?? fallbackName ?? recipe
);
